import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {isValidDate} from 'ApplicationEntry/models/Date';
import {InvalidZipCode, isValidZipCode, ZipCode} from 'ApplicationEntry/models/ZipCode';
import {Async} from 'Core/models/Async';
import {CaseNumber, CaseNumberError, UserAssignedCaseNumber} from './CaseNumber';
import {FormNumber, FormNumberError, UserAssignedFormNumber} from './FormNumber';

export class InvalidOrdinaryBillingAgent {
  public readonly key = 'InvalidOrdinaryBillingAgent';

  constructor(
    public readonly caseNumber: CaseNumber,
    public readonly controlNumber: StringUnion,
    public readonly caseName: StringUnion,
    public readonly transit: StringUnion,
    public readonly account: StringUnion,
    public readonly draftDay: StringUnion,
    public readonly billingMode: StringUnion,
    public readonly achTransitNumber: StringUnion,
    public readonly achAccountNumber: StringUnion,
    public readonly achAccountType: StringUnion,
    public readonly achPayerName: StringUnion,
    public readonly achDraftDay: StringUnion,
    public readonly achAuthorizationDate: Date | '',
    public readonly issueDate: Date | '',
    public readonly formNumber: FormNumber,
    public readonly payor: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ZipCode,
    public readonly signatureState: StringUnion,
    public readonly dateSigned: Date | '',
    public readonly ink: boolean,
    public readonly payroll: boolean,
    public readonly directBill: boolean,
    public readonly pac: boolean,
    public readonly floridaLicense: boolean,
    public readonly billingCode: StringUnion,
    public readonly transitNumber: StringUnion,
    public readonly bankAccountNumber: StringUnion,
    public readonly dueDate: StringUnion,
    public readonly accountHolderName: StringUnion,
    public readonly bankType: StringUnion,
    public readonly authorizedDate: Date | '',
  ) {}
}

type BillingAgentProp = {
  caseNumber: CaseNumber;
  controlNumber: StringUnion;
  caseName: StringUnion;
  transit: StringUnion;
  account: StringUnion;
  draftDay: StringUnion;
  billingMode: StringUnion;
  achTransitNumber: StringUnion,
  achAccountNumber: StringUnion,
  achAccountType: StringUnion,
  achPayerName: StringUnion,
  achDraftDay: StringUnion,
  achAuthorizationDate: Date | '',
  issueDate: Date | '';
  formNumber: FormNumber;
  payor: StringUnion;
  state: StringUnion;
  zip: ZipCode;
  signatureState: StringUnion;
  dateSigned: Date | '';
  ink: boolean;
  payroll: boolean;
  directBill: boolean;
  pac: boolean;
  floridaLicense: boolean;
  billingCode: StringUnion;
  transitNumber: StringUnion;
  bankAccountNumber: StringUnion;
  dueDate: StringUnion;
  accountHolderName: StringUnion;
  bankType: StringUnion;
  authorizedDate: Date | '';
};

export class ValidOrdinaryBillingAgent {
  public static createFromApp({
    caseNumber,
    controlNumber,
    caseName,
    transit,
    account,
    draftDay,
    billingMode,
    achTransitNumber,
    achAccountNumber,
    achAccountType,
    achPayerName,
    achDraftDay,
    achAuthorizationDate,
    issueDate,
    formNumber,
    payor,
    state,
    zip,
    signatureState,
    dateSigned,
    ink,
    payroll,
    directBill,
    pac,
    floridaLicense,
    billingCode,
    transitNumber,
    bankAccountNumber,
    dueDate,
    accountHolderName,
    bankType,
    authorizedDate,
  }: BillingAgentProp) {
    return this.create(
      caseNumber,
      controlNumber,
      caseName,
      transit,
      account,
      draftDay,
      billingMode,
      achTransitNumber,
      achAccountNumber,
      achAccountType,
      achPayerName,
      achDraftDay,
      achAuthorizationDate,
      issueDate,
      formNumber,
      payor,
      state,
      zip,
      signatureState,
      dateSigned,
      ink,
      payroll,
      directBill,
      pac,
      floridaLicense,
      billingCode,
      transitNumber,
      bankAccountNumber,
      dueDate,
      accountHolderName,
      bankType,
      authorizedDate
    );
  }

  public static create(
    caseNumber: CaseNumber,
    controlNumber: StringUnion,
    caseName: StringUnion,
    transit: StringUnion,
    account: StringUnion,
    draftDay: StringUnion,
    billingMode: StringUnion,
    achTransitNumber: StringUnion,
    achAccountNumber: StringUnion,
    achAccountType: StringUnion,
    achPayerName: StringUnion,
    achDraftDay: StringUnion,
    achAuthorizationDate: Date | '',
    issueDate: Date | '',
    formNumber: FormNumber,
    payor: StringUnion,
    state: StringUnion,
    zip: ZipCode,
    signatureState: StringUnion,
    dateSigned: Date | '',
    ink: boolean,
    payroll: boolean,
    directBill: boolean,
    pac: boolean,
    floridaLicense: boolean,
    billingCode: StringUnion,
    transitNumber: StringUnion,
    bankAccountNumber: StringUnion,
    dueDate: StringUnion,
    accountHolderName: StringUnion,
    bankType: StringUnion,
    authorizedDate: Date | '',
  ) {
    return isNonEmptyString(billingMode) && isValidZipCode(zip) && isValidDate(issueDate) && isValidDate(dateSigned) && isValidDate(achAuthorizationDate)
      ? new ValidOrdinaryBillingAgent(
          caseNumber,
          controlNumber,
          caseName,
          transit,
          account,
          draftDay,
          billingMode,
          achTransitNumber,
          achAccountNumber,
          achAccountType,
          achPayerName,
          achDraftDay,
          achAuthorizationDate,
          issueDate,
          formNumber,
          payor,
          state,
          zip,
          signatureState,
          dateSigned,
          ink,
          payroll,
          directBill,
          pac,
          floridaLicense,
          billingCode,
          transitNumber,
          bankAccountNumber,
          dueDate,
          accountHolderName,
          bankType,
          authorizedDate,
        )
      : new InvalidOrdinaryBillingAgent(
          caseNumber,
          controlNumber,
          caseName,
          transit,
          account,
          draftDay,
          billingMode,
          achTransitNumber,
          achAccountNumber,
          achAccountType,
          achPayerName,
          achDraftDay,
          achAuthorizationDate,
          issueDate,
          formNumber,
          payor,
          state,
          zip,
          signatureState,
          dateSigned,
          ink,
          payroll,
          directBill,
          pac,
          floridaLicense,
          billingCode,
          transitNumber,
          bankAccountNumber,
          dueDate,
          accountHolderName,
          bankType,
          authorizedDate
        );
  }
  public readonly key = 'ValidOrdinaryBillingAgent';

  constructor(
    public readonly caseNumber: CaseNumber,
    public readonly controlNumber: StringUnion,
    public readonly caseName: StringUnion,
    public readonly transit: StringUnion,
    public readonly account: StringUnion,
    public readonly draftDay: StringUnion,
    public readonly billingMode: StringUnion,
    public readonly achTransitNumber: StringUnion,
    public readonly achAccountNumber: StringUnion,
    public readonly achAccountType: StringUnion,
    public readonly achPayerName: StringUnion,
    public readonly achDraftDay: StringUnion,
    public readonly achAuthorizationDate: Date | '',
    public readonly issueDate: Date | '',
    public readonly formNumber: FormNumber,
    public readonly payor: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ZipCode,
    public readonly signatureState: StringUnion,
    public readonly dateSigned: Date | '',
    public readonly ink: boolean,
    public readonly payroll: boolean,
    public readonly directBill: boolean,
    public readonly pac: boolean,
    public readonly floridaLicense: boolean,
    public readonly billingCode: StringUnion,
    public readonly transitNumber: StringUnion,
    public readonly bankAccountNumber: StringUnion,
    public readonly dueDate: StringUnion,
    public readonly accountHolderName: StringUnion,
    public readonly bankType: StringUnion,
    public readonly authorizedDate: Date | '',
  ) {}
}

export type OrdinaryBillingAgent = ValidOrdinaryBillingAgent | InvalidOrdinaryBillingAgent;

export type OrdinaryBillingAgentOptions<TReturn> = ToMatchOptions<OrdinaryBillingAgent, 'key', TReturn>;

export const matchOrdinaryBillingAgent = <TReturn>(
  billingAgent: OrdinaryBillingAgent,
  options: OrdinaryBillingAgentOptions<TReturn>
): TReturn => {
  switch (billingAgent.key) {
    case 'InvalidOrdinaryBillingAgent':
      return options.InvalidOrdinaryBillingAgent(billingAgent);
    case 'ValidOrdinaryBillingAgent':
      return options.ValidOrdinaryBillingAgent(billingAgent);
  }
};

export const EmptyOrdinaryBillingAgent: OrdinaryBillingAgent = new ValidOrdinaryBillingAgent(
  new UserAssignedCaseNumber(''),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  '',
  '',
  new UserAssignedFormNumber('', '', '', '', ''),
  new EmptyString(),
  new EmptyString(),
  new InvalidZipCode(''),
  new EmptyString(),
  '',
  false,
  false,
  false,
  false,
  false,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  ''
);

export const changeCaseNumber = (caseNumber: Async<string>) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({...app, caseNumber});

export const changeUserCaseNumber = (caseNumber: string) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({...app, caseNumber: UserAssignedCaseNumber.create(caseNumber)});

export const changeCaseNumberError = (caseNumber: string) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({...app, caseNumber: CaseNumberError.create(caseNumber)});

export const changeControlNumber = (controlNumber: string) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({
    ...app,
    controlNumber: NonEmptyString.create(controlNumber),
  });

export const changeCaseName = (caseName: string) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({
    ...app,
    caseName: NonEmptyString.create(caseName),
  });

export const changeTransit = (transit: string) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({...app, transit: NonEmptyString.create(transit)});

export const changeAccount = (account: string) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({...app, account: NonEmptyString.create(account)});

export const changeDraftDay = (draftDay: string) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({...app, draftDay: NonEmptyString.create(draftDay)});

export const changeBillingMode = (billingMode: string) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({...app, billingMode: NonEmptyString.create(billingMode)});

export const changeState = (state: string) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({...app, state: NonEmptyString.create(state)});

export const changeZip = (zip: ZipCode) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({...app, zip});

export const changeFloridaLicense = (floridaLicense: boolean) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({...app, floridaLicense});

export const changeIssueDate = (issueDate: Date | '') => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({...app, issueDate});

export const changeFormNumber = (formNumber: Async<UserAssignedFormNumber>) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({
    ...app,
    formNumber,
  });

export const changeUserFormNumber = (
  formNumber: string,
  documentShortName: string,
  fipscoDocumentName: string,
  documentLongName: string,
  documentName: string
) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({
    ...app,
    formNumber: UserAssignedFormNumber.create(
      formNumber,
      documentShortName,
      fipscoDocumentName,
      documentShortName,
      fipscoDocumentName
    ),
  });

export const changeFormNumberError = (formNumber: string) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({...app, formNumber: FormNumberError.create(formNumber)});

export const changeDate = (dateSigned: Date | '') => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({...app, dateSigned});

export const changeAuthorizedDate = (achAuthorizationDate: Date | '') => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({...app, achAuthorizationDate});

export const changePayor = (payor: string) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({...app, payor: NonEmptyString.create(payor)});

export const changeSignatureState = (signatureState: string) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({...app, signatureState: NonEmptyString.create(signatureState)});

export const changeInk = (ink: boolean) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({...app, ink});

export const changePayroll = (payroll: boolean) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({...app, payroll});

export const changeDirectBill = (directBill: boolean) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({...app, directBill});

export const changePac = (pac: boolean) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({...app, pac});

export const changeAsyncBillingModeCaseNumber = (billingCode: string) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({
    ...app,
    billingCode: NonEmptyString.create(billingCode),
  });
export const changeAsyncCaseNumber = (caseNumber: Async<string>) => (app: OrdinaryBillingAgent) =>
  ValidOrdinaryBillingAgent.createFromApp({
    ...app,
    caseNumber,
  });
